import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Check, Briefcase, Home, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import VoiceTextInput from "../components/voice/VoiceTextInput";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { api, formatApiError } from "../lib/api";
import { LANGUAGE_OPTIONS, toIsoCode } from "../lib/languageOptions";

const ROLE_KEYS = [
  "engineer", "pm", "data", "designer", "founder", "marketing",
  "researcher", "student", "lawyer", "doctor", "finance", "ops", "other",
];
const TONE_KEYS = ["professional", "friendly", "technical", "socratic"];
const PRONOUN_KEYS = ["she_her", "he_him", "they_them", "prefer_not_to_say"];
const PROFILE_TYPES = ["personal", "work", "mixed"];
const INDUSTRIES = [
  "tech", "finance", "legal", "healthcare", "education", "government",
  "media", "retail", "manufacturing", "real_estate", "consulting",
  "non_profit", "construction", "hospitality", "other",
];
const COMPANY_SIZES = ["1", "2-10", "11-50", "51-200", "201-1000", "1000+"];
const SENIORITIES = ["junior", "mid", "senior", "lead", "executive", "founder"];
const PRIMARY_GOALS = [
  "summarize_documents", "brainstorm", "translate", "research",
  "draft_writing", "voice_notes", "code_help", "data_analysis", "other",
];
const AI_FAMILIARITY = ["never", "casual", "power"];
const PREFERRED_LENGTHS = ["short", "medium", "long"];
const READING_LEVELS = ["plain", "standard", "technical"];

// Sample country list — backend returns the canonical full list at /me/profile-options.
const FALLBACK_COUNTRIES = [
  "AR", "BR", "CL", "CO", "MX", "PE", "UY", "VE", "EC", "BO", "PY",
  "US", "CA", "ES", "GB", "FR", "DE", "IT", "PT", "NL", "BE", "CH",
  "AU", "NZ", "JP", "KR", "CN", "IN", "ID", "PH", "TH", "VN",
  "ZA", "NG", "EG", "AE", "SA", "IL", "TR",
];

export default function Onboarding() {
  const { t, i18n } = useTranslation();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  const appName = branding.app_name || "Faro";

  const initial = user?.context_profile || {};
  const defaultLang = (i18n.language || "es-AR").startsWith("es") ? "es" : "en";

  // ----- Step state -----
  const [step, setStep] = useState(initial.onboarding_step || 1);
  const [busy, setBusy] = useState(false);

  // ----- Step 1 · Identity -----
  const [dateOfBirth, setDateOfBirth] = useState(initial.date_of_birth || "");
  const [country, setCountry] = useState(initial.country || "");
  const [pronouns, setPronouns] = useState(initial.pronouns || "");

  // ----- Step 2 · Profile type -----
  const [profileType, setProfileType] = useState(initial.profile_type || "");

  // ----- Step 3 · Work context (skipped if profile_type === 'personal') -----
  const [company, setCompany] = useState(initial.company || "");
  const [industry, setIndustry] = useState(initial.industry || "");
  const [companySize, setCompanySize] = useState(initial.company_size || "");
  const [seniority, setSeniority] = useState(initial.seniority || "");
  const [workEmail, setWorkEmail] = useState(initial.work_email || "");

  // ----- Step 4 · Personalization -----
  const [roleKey, setRoleKey] = useState(initial.role_key || "");
  const [background, setBackground] = useState(initial.background || "");
  const [tone, setTone] = useState(initial.tone || "professional");
  const [language, setLanguage] = useState(toIsoCode(initial.language) || defaultLang);

  // Re-seed language once i18n has hydrated, when the user has no prior
  // context_profile.language (fresh signups under es-AR were sometimes
  // landing on 'en' due to a first-paint race with the i18n bundle).
  useEffect(() => {
    if (!initial.language) {
      const seeded = (i18n.language || "es-AR").startsWith("es") ? "es" : "en";
      setLanguage(seeded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
  const [primaryGoals, setPrimaryGoals] = useState(initial.primary_goals || []);
  const [aiFamiliarity, setAiFamiliarity] = useState(initial.ai_familiarity || "");
  const [preferredLength, setPreferredLength] = useState(initial.preferred_length || "");
  const [readingLevel, setReadingLevel] = useState(initial.reading_level || "");
  const [reduceMotion, setReduceMotion] = useState(initial.reduce_motion === true);

  // ----- Server-driven enum lists -----
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  useEffect(() => {
    api.get("/me/profile-options").then(({ data }) => {
      if (Array.isArray(data?.countries) && data.countries.length) {
        setCountries(data.countries);
      }
    }).catch(() => { /* fallback list already loaded */ });
  }, []);

  // Profile-type 'personal' skips work-context (step 3 → step 4 directly).
  const totalSteps = profileType === "personal" ? 3 : 4;
  const visibleStep = (() => {
    // Renumber for the progress UI: when totalSteps is 3, step "4" is actually shown as "3".
    if (totalSteps === 3 && step === 4) return 3;
    return Math.min(step, totalSteps);
  })();

  const toggleGoal = (g) => {
    setPrimaryGoals((cur) => {
      if (cur.includes(g)) return cur.filter((x) => x !== g);
      if (cur.length >= 3) return cur;  // cap at 3
      return [...cur, g];
    });
  };

  const saveStep = async (nextStep, completed = false) => {
    setBusy(true);
    try {
      const payload = {
        // Step 1
        date_of_birth: dateOfBirth || undefined,
        country: country || undefined,
        pronouns: pronouns || undefined,
        // Step 2
        profile_type: profileType || undefined,
        // Step 3
        company: company || undefined,
        industry: industry || undefined,
        company_size: companySize || undefined,
        seniority: seniority || undefined,
        work_email: workEmail || undefined,
        // Step 4
        role_key: roleKey || undefined,
        role_label: roleKey ? t(`account:onboarding.roles.${roleKey}`) : undefined,
        background: background || undefined,
        tone,
        language,
        primary_goals: primaryGoals.length ? primaryGoals : undefined,
        ai_familiarity: aiFamiliarity || undefined,
        preferred_length: preferredLength || undefined,
        reading_level: readingLevel || undefined,
        reduce_motion: reduceMotion,
        // Wizard control
        onboarding_step: nextStep,
        onboarding_completed: completed || undefined,
      };
      // strip undefined to keep PATCH-like semantics
      const clean = Object.fromEntries(Object.entries(payload).filter(([_, v]) => v !== undefined));
      const { data } = await api.put("/auth/profile", clean);
      setUser(data);
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // ----- Navigation -----
  const goNext = async () => {
    // Per-step validation
    if (step === 2 && !profileType) {
      return toast.error(t("account:onboarding.pick_profile_type", "Elegí una opción"));
    }
    if (step === 4 && !roleKey) {
      return toast.error(t("account:onboarding.pick_role", "Elegí un rol"));
    }

    let nextStep = step + 1;
    // Skip work-context when personal
    if (step === 2 && profileType === "personal") nextStep = 4;

    if (nextStep > 4) {
      // Final submit
      const ok = await saveStep(4, true);
      if (ok) {
        toast.success(t("account:onboarding.saved", "Perfil guardado — empecemos a chatear"));
        navigate("/workspace");
      }
      return;
    }
    const ok = await saveStep(nextStep);
    if (ok) setStep(nextStep);
  };

  const goBack = () => {
    let prev = step - 1;
    if (step === 4 && profileType === "personal") prev = 2;
    if (prev < 1) prev = 1;
    setStep(prev);
  };

  const saveAndContinueLater = async () => {
    const ok = await saveStep(step);
    if (ok) {
      toast.success(t("account:onboarding.saved_partial", "Listo, podés continuar cuando quieras"));
      navigate("/workspace");
    }
  };

  return (
    <div className="min-h-screen bg-background grid place-items-center p-6">
      <div className="w-full max-w-2xl border border-border bg-card p-8 lg:p-12">
        {/* Progress bar */}
        <div className="flex items-center gap-2 mb-6">
          {Array.from({ length: totalSteps }).map((_, idx) => {
            const n = idx + 1;
            const active = n <= visibleStep;
            return (
              <div key={n} className="flex-1 h-1 rounded-sm overflow-hidden bg-secondary">
                <div
                  className={`h-full transition-all ${active ? "bg-primary" : "bg-transparent"}`}
                  style={{ width: active ? "100%" : "0%" }}
                />
              </div>
            );
          })}
        </div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono mb-2">
          [ {t("account:onboarding.step_label", { step: visibleStep, total: totalSteps, defaultValue: "paso {{step}} de {{total}}" })} ]
        </div>

        {step === 1 && (
          <Step1Identity
            t={t} appName={appName}
            dateOfBirth={dateOfBirth} setDateOfBirth={setDateOfBirth}
            country={country} setCountry={setCountry}
            pronouns={pronouns} setPronouns={setPronouns}
            countries={countries}
          />
        )}
        {step === 2 && (
          <Step2ProfileType
            t={t} profileType={profileType} setProfileType={setProfileType}
          />
        )}
        {step === 3 && (
          <Step3WorkContext
            t={t}
            company={company} setCompany={setCompany}
            industry={industry} setIndustry={setIndustry}
            companySize={companySize} setCompanySize={setCompanySize}
            seniority={seniority} setSeniority={setSeniority}
            workEmail={workEmail} setWorkEmail={setWorkEmail}
          />
        )}
        {step === 4 && (
          <Step4Personalization
            t={t}
            roleKey={roleKey} setRoleKey={setRoleKey}
            background={background} setBackground={setBackground}
            tone={tone} setTone={setTone}
            language={language} setLanguage={setLanguage}
            primaryGoals={primaryGoals} toggleGoal={toggleGoal}
            aiFamiliarity={aiFamiliarity} setAiFamiliarity={setAiFamiliarity}
            preferredLength={preferredLength} setPreferredLength={setPreferredLength}
            readingLevel={readingLevel} setReadingLevel={setReadingLevel}
            reduceMotion={reduceMotion} setReduceMotion={setReduceMotion}
          />
        )}

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-8 mt-6 border-t border-border">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={step === 1 || busy}
            className="rounded-sm"
            data-testid="onboarding-back-button"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
            {t("account:onboarding.back", "Atrás")}
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={saveAndContinueLater}
              disabled={busy}
              className="rounded-sm text-muted-foreground"
              data-testid="onboarding-save-later-button"
            >
              {t("account:onboarding.save_later", "Guardar y continuar luego")}
            </Button>
            <Button
              onClick={goNext}
              disabled={busy}
              className="rounded-sm"
              data-testid="onboarding-next-button"
            >
              {step === 4 || (step === 2 && profileType === "personal" && false /* keep "Next" labelling consistent */) ? null : null}
              {step === 4 ? (
                <>
                  {busy ? t("account:onboarding.saving", "Guardando…") : t("account:onboarding.finish", "Finalizar")}
                  <Check className="h-3.5 w-3.5 ml-1.5" />
                </>
              ) : (
                <>
                  {busy ? t("account:onboarding.saving", "Guardando…") : t("account:onboarding.next", "Siguiente")}
                  <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// STEP COMPONENTS
// ---------------------------------------------------------------------------

function Step1Identity({ t, appName, dateOfBirth, setDateOfBirth, country, setCountry, pronouns, setPronouns, countries }) {
  return (
    <>
      <h1 className="font-display font-black tracking-tight text-3xl lg:text-4xl">
        {t("account:onboarding.step1.title", "Contanos un poco sobre vos")}
      </h1>
      <p className="mt-2 text-muted-foreground">
        {t("account:onboarding.step1.subtitle", { app: appName, defaultValue: "{{app}} usa estos datos para adaptar el tono y el contenido — todo opcional." })}
      </p>
      <div className="mt-8 space-y-5" data-testid="onboarding-step-1">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.dob", "Fecha de nacimiento")} <span className="text-muted-foreground normal-case">· {t("account:onboarding.optional", "opcional")}</span>
            </Label>
            <Input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="mt-1.5 rounded-sm h-11"
              data-testid="onboarding-dob-input"
            />
            <p className="text-[11px] font-mono text-muted-foreground mt-1">
              {t("account:onboarding.dob_hint", "Nos ayuda a calibrar el lenguaje según tu edad.")}
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.country", "País")} <span className="text-muted-foreground normal-case">· {t("account:onboarding.optional", "opcional")}</span>
            </Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-country-select">
                <SelectValue placeholder={t("account:onboarding.country_placeholder", "Elegí tu país")} />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {countries.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`onboarding-country-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-mono">
            {t("account:onboarding.pronouns", "Pronombres")} <span className="text-muted-foreground normal-case">· {t("account:onboarding.optional", "opcional")}</span>
          </Label>
          <Select value={pronouns} onValueChange={setPronouns}>
            <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-pronouns-select">
              <SelectValue placeholder={t("account:onboarding.pronouns_placeholder", "Cómo te tratamos")} />
            </SelectTrigger>
            <SelectContent>
              {["she_her", "he_him", "they_them", "prefer_not_to_say"].map((p) => (
                <SelectItem key={p} value={p} data-testid={`onboarding-pronoun-${p}`}>
                  {t(`account:onboarding.pronouns_options.${p}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
}

function Step2ProfileType({ t, profileType, setProfileType }) {
  const opts = [
    { key: "personal", icon: Home,      titleKey: "account:onboarding.profile_types.personal.title",
      descKey: "account:onboarding.profile_types.personal.desc" },
    { key: "work",     icon: Briefcase, titleKey: "account:onboarding.profile_types.work.title",
      descKey: "account:onboarding.profile_types.work.desc" },
    { key: "mixed",    icon: Sparkles,  titleKey: "account:onboarding.profile_types.mixed.title",
      descKey: "account:onboarding.profile_types.mixed.desc" },
  ];
  return (
    <>
      <h1 className="font-display font-black tracking-tight text-3xl lg:text-4xl">
        {t("account:onboarding.step2.title", "¿Para qué vas a usar Faro?")}
      </h1>
      <p className="mt-2 text-muted-foreground">
        {t("account:onboarding.step2.subtitle", "Adaptamos la app a tu uso principal. Podés cambiarlo después.")}
      </p>
      <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="onboarding-step-2">
        {opts.map(({ key, icon: Icon, titleKey, descKey }) => {
          const selected = profileType === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setProfileType(key)}
              data-testid={`onboarding-profile-type-${key}`}
              className={`text-left p-4 border-2 rounded-sm transition-all ${
                selected ? "border-primary bg-primary/5" : "border-border bg-card hover:border-foreground/30"
              }`}
            >
              <Icon className={`h-5 w-5 mb-3 ${selected ? "text-primary" : "text-muted-foreground"}`} />
              <div className="font-semibold mb-1">{t(titleKey)}</div>
              <div className="text-xs text-muted-foreground">{t(descKey)}</div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function Step3WorkContext({ t, company, setCompany, industry, setIndustry, companySize, setCompanySize, seniority, setSeniority, workEmail, setWorkEmail }) {
  return (
    <>
      <h1 className="font-display font-black tracking-tight text-3xl lg:text-4xl">
        {t("account:onboarding.step3.title", "Tu contexto laboral")}
      </h1>
      <p className="mt-2 text-muted-foreground">
        {t("account:onboarding.step3.subtitle", "Si querés que el asistente entienda mejor tu trabajo, contanos un poco.")}
      </p>
      <div className="mt-8 space-y-5" data-testid="onboarding-step-3">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.company", "Empresa / organización")}
            </Label>
            <Input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder={t("account:onboarding.company_placeholder", "Ej: Shoppias")}
              className="mt-1.5 rounded-sm h-11"
              data-testid="onboarding-company-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.industry", "Industria")}
            </Label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-industry-select">
                <SelectValue placeholder={t("account:onboarding.industry_placeholder", "Elegí una")} />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`onboarding-industry-${k}`}>
                    {t(`account:onboarding.industries.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.company_size", "Tamaño de la empresa")}
            </Label>
            <Select value={companySize} onValueChange={setCompanySize}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-company-size-select">
                <SelectValue placeholder={t("account:onboarding.optional", "opcional")} />
              </SelectTrigger>
              <SelectContent>
                {COMPANY_SIZES.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`onboarding-company-size-${k}`}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.seniority", "Seniority")}
            </Label>
            <Select value={seniority} onValueChange={setSeniority}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-seniority-select">
                <SelectValue placeholder={t("account:onboarding.optional", "opcional")} />
              </SelectTrigger>
              <SelectContent>
                {SENIORITIES.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`onboarding-seniority-${k}`}>
                    {t(`account:onboarding.seniorities.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-mono">
            {t("account:onboarding.work_email", "Email laboral")} <span className="text-muted-foreground normal-case">· {t("account:onboarding.optional", "opcional")}</span>
          </Label>
          <Input
            type="email"
            value={workEmail}
            onChange={(e) => setWorkEmail(e.target.value)}
            placeholder="vos@empresa.com"
            className="mt-1.5 rounded-sm h-11"
            data-testid="onboarding-work-email-input"
          />
        </div>
      </div>
    </>
  );
}

function Step4Personalization({
  t, roleKey, setRoleKey, background, setBackground, tone, setTone, language, setLanguage,
  primaryGoals, toggleGoal, aiFamiliarity, setAiFamiliarity, preferredLength, setPreferredLength,
  readingLevel, setReadingLevel, reduceMotion, setReduceMotion,
}) {
  return (
    <>
      <h1 className="font-display font-black tracking-tight text-3xl lg:text-4xl">
        {t("account:onboarding.step4.title", "Personalizá tu asistente")}
      </h1>
      <p className="mt-2 text-muted-foreground">
        {t("account:onboarding.step4.subtitle", "El último paso — cómo querés que te trate Faro.")}
      </p>
      <div className="mt-8 space-y-5" data-testid="onboarding-step-4">
        <div>
          <Label className="text-xs uppercase tracking-wider font-mono">
            {t("account:onboarding.role", "Tu rol")}
          </Label>
          <Select value={roleKey} onValueChange={setRoleKey}>
            <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-role-select">
              <SelectValue placeholder={t("account:onboarding.role_placeholder", "Elegí la opción más cercana…")} />
            </SelectTrigger>
            <SelectContent>
              {ROLE_KEYS.map((k) => (
                <SelectItem key={k} value={k} data-testid={`onboarding-role-${k}`}>{t(`account:onboarding.roles.${k}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-mono">
            {t("account:onboarding.background", "Una nota corta sobre tu background")}
          </Label>
          <div className="mt-1.5">
            <VoiceTextInput
              value={background}
              onChange={setBackground}
              rows={4}
              maxLength={500}
              language={language}
              callerContext="onboarding"
              placeholder={t("account:onboarding.background_placeholder", "Ej: 10 años en backend, hoy armando una fintech en Buenos Aires. Me gustan las respuestas concisas y con ejemplos.")}
              testid="onboarding-background-voice"
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.tone", "Tono preferido")}
            </Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-tone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONE_KEYS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`onboarding-tone-${k}`}>{t(`account:onboarding.tones.${k}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.language", "Idioma de las respuestas")}
            </Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`onboarding-language-option-${opt.value}`}>
                    {t(opt.labelKey, opt.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider font-mono">
            {t("account:onboarding.primary_goals", "¿Para qué usás IA?")} <span className="text-muted-foreground normal-case">· {t("account:onboarding.max_3", "hasta 3")}</span>
          </Label>
          <div className="flex flex-wrap gap-2 mt-1.5" data-testid="onboarding-goals">
            {PRIMARY_GOALS.map((g) => {
              const on = primaryGoals.includes(g);
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGoal(g)}
                  className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                    on ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-foreground border-border hover:border-foreground/40"
                  }`}
                  data-testid={`onboarding-goal-${g}`}
                >
                  {t(`account:onboarding.goals.${g}`)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.ai_familiarity", "Familiaridad con IA")}
            </Label>
            <Select value={aiFamiliarity} onValueChange={setAiFamiliarity}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-ai-familiarity-select">
                <SelectValue placeholder={t("account:onboarding.optional", "opcional")} />
              </SelectTrigger>
              <SelectContent>
                {AI_FAMILIARITY.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`onboarding-ai-familiarity-${k}`}>
                    {t(`account:onboarding.ai_familiarity_options.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.preferred_length", "Largo de respuestas")}
            </Label>
            <Select value={preferredLength} onValueChange={setPreferredLength}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-preferred-length-select">
                <SelectValue placeholder={t("account:onboarding.optional", "opcional")} />
              </SelectTrigger>
              <SelectContent>
                {PREFERRED_LENGTHS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`onboarding-preferred-length-${k}`}>
                    {t(`account:onboarding.preferred_length_options.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.reading_level", "Nivel de lectura")}
            </Label>
            <Select value={readingLevel} onValueChange={setReadingLevel}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="onboarding-reading-level-select">
                <SelectValue placeholder={t("account:onboarding.optional", "opcional")} />
              </SelectTrigger>
              <SelectContent>
                {READING_LEVELS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`onboarding-reading-level-${k}`}>
                    {t(`account:onboarding.reading_level_options.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="onboarding-reduce-motion-label">
          <input
            type="checkbox"
            checked={reduceMotion}
            onChange={(e) => setReduceMotion(e.target.checked)}
            className="h-4 w-4"
            data-testid="onboarding-reduce-motion-checkbox"
          />
          <span>{t("account:onboarding.reduce_motion", "Reducir animaciones de la interfaz")}</span>
        </label>
      </div>
    </>
  );
}
